import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { Input, Modal } from 'antd';
import {
  FormGroup,
  Grid,
  RemoteRequest,
  Segment,
  useUserinfo,
} from '@mss/mss-component-library';
import Api from '../services/Api';
import dateFormat from 'dateformat';
import classNames from 'classnames';

type RequestAdminModalProps = {
  closeModal: any;
  request: RemoteRequest;
};

type GridRowProps = {
  border?: boolean;
};

export const GridRow = ({
  className,
  border,
  ...props
}: React.ComponentProps<typeof Grid.Row> & GridRowProps) => {
  return (
    <Grid.Row
      className={classNames(
        'm-0',
        {
          'border-b-0': border === false,
          'border-b border-gray-200': border !== false,
        },
        className
      )}
      {...props}
    />
  );
};

export const GridCol = ({
  className,
  ...props
}: React.ComponentProps<typeof Grid.Col>) => {
  return (
    <Grid.Col className={classNames('py-[5px] px-4', className)} {...props} />
  );
};

const RequestAdminModal = ({ closeModal, request }: RequestAdminModalProps) => {
  const [t] = useTranslation(['remoteAccess', 'asset', 'common']);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasPermission] = useUserinfo();

  async function decline() {
    setLoading(true);
    await Api.request.update.decline(request.id, msg);
    closeModal();
  }

  async function approve() {
    setLoading(true);
    await Api.request.update.approve(request.id, msg);
    closeModal();
  }

  async function revoke() {
    setLoading(true);
    await Api.request.update.revoke(request.id, msg);
    closeModal();
  }

  let cancelText = '';
  let cancelHide = false;
  let okHide = false;
  let cancelRevoke = false;
  let cancelDecline = false;

  if (hasPermission('admin_update_remoterequest')) {
    switch (request.status) {
      case 'awaiting_approval':
        cancelText = t('Deny');
        cancelDecline = true;
        break;
      case 'allow':
        cancelText = t('Revoke');
        okHide = true;
        cancelRevoke = true;
        break;
      case 'wait_for_start_date':
        cancelText = t('Revoke');
        cancelRevoke = true;
        okHide = true;
        break;
      case 'denied':
        cancelHide = true;
        break;
      case 'revoked':
        cancelHide = true;
        break;
      case 'expired':
        okHide = true;
        cancelHide = true;
        break;
      case 'expired_unapproved':
        okHide = true;
        cancelHide = true;
        break;
    }
  } else {
    cancelHide = true;
    okHide = true;
  }

  return (
    <Modal
      title={t('Remote request approval')}
      okText={t('Approve')}
      onCancel={closeModal}
      cancelText={cancelText}
      onOk={approve}
      okButtonProps={{
        style: okHide ? { display: 'none' } : undefined,
        loading,
        className: 'remote-request-ok-btn',
      }}
      cancelButtonProps={{
        style: cancelHide ? { display: 'none' } : undefined,
        onClick: e => {
          e.stopPropagation();
          if (cancelRevoke) {
            revoke();
          }

          if (cancelDecline) {
            decline();
          }
        },
        loading,
        danger: true,
        type: 'primary',
      }}
      visible
      width={800}
    >
      <Grid.Container>
        <FormGroup
          label={'Request details'}
          input={
            <Segment border>
              <GridRow>
                <GridCol width={4}>{t('ID')}</GridCol>
                <GridCol width={8}>{request.id}</GridCol>
              </GridRow>
              <GridRow>
                <GridCol width={4}>{t('User')}</GridCol>
                <GridCol width={8}>{request.request_user_object.name}</GridCol>
              </GridRow>
              <GridRow>
                <GridCol width={4}>{t('Target asset')}</GridCol>
                <GridCol width={8}>{request.asset_object.name}</GridCol>
              </GridRow>
              <GridRow>
                <GridCol width={4}>{t('Start Time')}</GridCol>
                <GridCol width={8}>
                  {request.approved_start_date &&
                    dateFormat(
                      request.approved_start_date || '',
                      'mmmm dS, yyyy @ h:MM:ss TT'
                    )}
                </GridCol>
              </GridRow>
              <GridRow>
                <GridCol width={4}>{t('End Time')}</GridCol>
                <GridCol width={8}>
                  {request.approved_end_date &&
                    dateFormat(
                      request.approved_end_date || '',
                      'mmmm dS, yyyy @ h:MM:ss TT'
                    )}
                </GridCol>
              </GridRow>
              <GridRow>
                <GridCol width={4}>{t('Ticket / Call ID')}</GridCol>
                <GridCol width={8}>{request.ticket_id}</GridCol>
              </GridRow>
              <GridRow>
                <GridCol width={4}>{t('Reason for connecting')}</GridCol>
                <GridCol width={8}>{request.request_message}</GridCol>
              </GridRow>
              <GridRow border={false}>
                <GridCol width={4}>{t('Message from administrator')}</GridCol>
                <GridCol width={8}>{request.approval_message}</GridCol>
              </GridRow>
            </Segment>
          }
        />
        {hasPermission('admin_update_remoterequest') && (
          <FormGroup
            label={t('Message to user')}
            input={
              <Input.TextArea
                rows={4}
                onChange={e => setMsg(e.target.value)}
                value={msg}
              />
            }
          />
        )}
      </Grid.Container>
    </Modal>
  );
};

export default RequestAdminModal;
